// src/pages/business/BusinessEmployeeInformation.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-regular-svg-icons";
import {
    getEmployeeData,
    updateEmployeeData,
    getBusinessById,
    getBusinessConfig,
} from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";
import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import {getEmployeeReadinessIssue, isBusinessActive} from "../../lib/onboarding";
import {setEmployeeStatus} from "../../services/onboardingService";


const BusinessEmployeeInformationV2 = () => {
    const { employeeID } = useParams();
    const { userData } = useAuth();

    const [formData, setFormData] = useState(null);
    const [biz, setBiz] = useState(null);
    const [config, setConfig] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [actionError, setActionError] = useState("");
    const [statusChoice, setStatusChoice] = useState("");

    // Load business + config + employee
    useEffect(() => {
        const load = async () => {
            if (!userData?.businessID) return;
            const [bizDoc, cfg, emp] = await Promise.all([
                getBusinessById(userData.businessID),
                getBusinessConfig(userData.businessID),
                getEmployeeData(userData.businessID, employeeID),
            ]);
            setBiz(bizDoc || {});
            // fallback if config missing
            const roles = cfg?.roles?.length ? cfg.roles : [
                { role: "server", weight: 1.0 },
                { role: "chef", weight: 1.0 },
                { role: "runner", weight: 1.0 },
                { role: "dishwasher", weight: 1.0 },
                { role: "bartender", weight: 1.0 },
            ];
            setConfig({ ...(cfg || {}), roles });
            setFormData(emp || {});
            setStatusChoice(emp?.accountStatus || "unverified");
        };
        load();
    }, [userData, employeeID]);

    const roleOptions = useMemo(
        () => (config?.roles || []).map((r) => (r.role || "").trim().toLowerCase()).filter(Boolean),
        [config]
    );

    if (!formData || !biz || !config) return (
        <BusinessLayoutV2>
            <div className="max-w-5xl mx-auto py-8">
                <div className="p-6">Loading...</div>
            </div>
        </BusinessLayoutV2>
    );

    const isDeactivated = formData.accountStatus === "deactivated";
    const readinessIssue = getEmployeeReadinessIssue(formData, {...biz, config});
    const payoutReady = !readinessIssue;
    const businessActive = isBusinessActive(biz);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRoleChange = (e) => {
        const role = e.target.value.trim().toLowerCase();
        setFormData((prev) => ({ ...prev, role }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const storage = getStorage();
        const storageRef = ref(storage, `employeeImages/${userData.businessID}_${employeeID}_${file.name}`);
        try {
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setFormData((prev) => ({ ...prev, imgUrl: url }));
        } catch (error) {
            console.error("Error uploading image:", error);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setActionError("");
        // sanitize outgoing payload (avoid undefined to Firestore)
        const {
            displayName = "",
            firstName = "",
            lastName = "",
            email = "",
            iban = "",
            role = "",
            imgUrl = formData.imgUrl || "",
        } = formData;

        const payload = {
            displayName,
            firstName,
            lastName,
            email,
            iban,
            role: role ? role.toLowerCase() : "",
            imgUrl,
        };
        try {
            await updateEmployeeData(userData.businessID, employeeID, payload);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (error) {
            setActionError(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleStatusUpdate = async (nextStatus = statusChoice) => {
        setIsSaving(true);
        setActionError("");
        try {
            await setEmployeeStatus(userData.businessID, employeeID, nextStatus);
            setFormData((current) => ({
                ...current,
                accountStatus: nextStatus,
                verified: nextStatus === "active" ? true : current.verified,
            }));
            setStatusChoice(nextStatus);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (error) {
            setActionError(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <BusinessLayoutV2>
            <style>{`
                .biz-emp-info-v2 { width: 100%; padding: 32px 0 80px; }
                .biz-emp-info-v2 .surface { width:min(100%,560px); background:#fff; border:1px solid #e8e8ee; border-radius:18px; padding:24px; }
                .biz-emp-info-v2 .title { font-size:28px; font-weight:700; letter-spacing:-0.5px; margin-bottom:14px; }
            `}</style>
            <div className="biz-emp-info-v2">
            <div className="max-w-5xl mx-auto">
                <h1 className="title">{formData.displayName || `${formData.firstName || ""} ${formData.lastName || ""}`.trim() || "Employee"} Information</h1>
                <div className="surface">
                <div className="space-y-5">

                    {/* Avatar */}
                    <div className="flex flex-col items-center mb-8">
                        <img
                            src={formData.imgUrl || "https://i.pravatar.cc/100"}
                            alt="avatar"
                            className="w-32 h-32 rounded-full border object-cover"
                        />
                        <label
                            htmlFor="avatar-upload"
                            className="mt-4 px-3 py-1 text-sm border rounded-full text-gray-700 cursor-pointer hover:bg-gray-100"
                        >
                            Change picture <FontAwesomeIcon icon={faEdit} />
                        </label>
                        <input
                            id="avatar-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                        />
                    </div>

                    {/* Deactivated banner */}
                    {isDeactivated && (
                        <p className="text-red-600 font-semibold text-center mb-4">
                            This employee is deactivated and cannot receive tips. You can still update their details and reactivate them.
                        </p>
                    )}

                    {/* Payout readiness */}
                    <div className="p-3 border rounded-md bg-gray-50">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">Payout status</div>
                            <div
                                className={`px-2 py-1 rounded text-xs border ${
                                    payoutReady
                                        ? "bg-green-100 text-green-800 border-green-300"
                                        : "bg-yellow-100 text-yellow-800 border-yellow-300"
                                }`}
                                title={
                                    payoutReady
                                        ? "Employee has a legal name, valid IBAN, and configured payout role."
                                        : readinessIssue
                                }
                            >
                                {payoutReady ? "Payout ready" : readinessIssue}
                            </div>
                        </div>
                    </div>

                    {/* Core fields */}
                    {[
                        { name: "displayName", label: "Display name" },
                        { name: "firstName", label: "First name" },
                        { name: "lastName", label: "Last name" },
                        { name: "email", label: "Email (optional)", type: "email" },
                        { name: "iban", label: "IBAN" },
                    ].map(({ name, label, type }) => (
                        <div key={name}>
                            <label className="block text-sm font-medium text-gray-700">{label}</label>
                            <input
                                name={name}
                                type={type || "text"}
                                value={formData[name] || ""}
                                onChange={handleChange}
                                className="w-full mt-1 p-2 border rounded-md"
                            />
                        </div>
                    ))}

                    {/* Role selector (from Business Config roles) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Role</label>
                        <select
                            name="role"
                            value={(formData.role || "").toLowerCase()}
                            onChange={handleRoleChange}
                            className="w-full mt-1 p-2 border rounded-md capitalize"
                        >
                            <option value="">Choose role…</option>
                            {roleOptions.map((r) => (
                                <option key={r} value={r} className="capitalize">
                                    {r}
                                </option>
                            ))}
                        </select>
                        {!roleOptions.includes((formData.role || "").toLowerCase()) && formData.role && (
                            <p className="text-xs text-amber-700 mt-1">
                                This role is not defined in Business Configuration. Add it there or choose another role.
                            </p>
                        )}
                    </div>

                    {/* Save */}
                    <button
                        className="w-full mt-6 py-2 bg-black text-white font-semibold rounded-md hover:bg-neutral-800 disabled:opacity-50"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                    {saveSuccess && <p className="text-green-600 text-sm mt-2">✅ Changes saved!</p>}
                    {actionError && <p className="text-red-700 text-sm mt-2">{actionError}</p>}

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-sm font-semibold text-gray-800">Tipping status</p>
                        {formData.accountStatus === "unverified" ? (
                            <div className="mt-2">
                                <p className="text-sm text-gray-600">
                                    This employee is a private draft and cannot receive tips yet. {businessActive ? "Save payout details, then activate them." : "Payout-ready drafts will be activated when Tiply approves the business."}
                                </p>
                                {businessActive && (
                                    <button type="button" onClick={() => handleStatusUpdate("active")} disabled={isSaving || !payoutReady} className="mt-3 rounded-lg bg-[#0d7c5f] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Activate employee</button>
                                )}
                            </div>
                        ) : (
                            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                                <select value={statusChoice} onChange={(event) => setStatusChoice(event.target.value)} className="flex-1 rounded-lg border border-gray-300 bg-white p-2 text-sm">
                                    <option value="active">Active — can receive tips</option>
                                    <option value="readonly">Read-only — tipping paused</option>
                                    <option value="deactivated">Deactivated</option>
                                </select>
                                <button type="button" onClick={() => handleStatusUpdate()} disabled={isSaving || statusChoice === formData.accountStatus || (statusChoice === "active" && !payoutReady)} className="rounded-lg border border-[#0d7c5f] px-4 py-2 text-sm font-semibold text-[#0d7c5f] disabled:opacity-50">Update status</button>
                            </div>
                        )}
                    </div>

                    {/*<div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 rounded-md shadow-sm">*/}
                    {/*    Roles are defined in <b>Business Configuration → Role Weights</b>. Ensure IBAN is present for payouts.*/}
                    {/*</div>*/}
                </div>
                </div>
            </div>
            </div>
        </BusinessLayoutV2>
    );
};

export default BusinessEmployeeInformationV2;
